import { API_ROUTES } from '@lucky/shared/constants'
import axios, { AxiosInstance } from 'axios'
import type {
    User,
    Guild,
    Module,
    ServerSettings,
    ServerListing,
    Feature,
    GlobalFeatureToggleResponse,
    GuildMemberContext,
    GuildRoleOption,
    RoleGrant,
    ModuleKey,
    GuildChannelOption,
    GuildEmojiOption,
} from '@/types'
import { ApiError } from './ApiError'
import { createMusicApi } from './musicApi'
import { createModerationApi } from './moderationApi'
import { createAutoModApi } from './automodApi'
import { createLogsApi } from './logsApi'
import { createAutoMessagesApi } from './autoMessagesApi'
import { createEmbedsApi } from './embedsApi'
import { createReactionRolesApi } from './reactionRolesApi'
import { createAutomationApi } from './automationApi'
import { createLevelsApi } from './levelsApi'
import { createArtistsApi } from './artistsApi'
import { createSupportApi } from './supportApi'
import { inferApiBase } from './apiBase'
import { createRolesManageApi } from './rolesManageApi'
import { createRoleGroupsApi } from './roleGroupsApi'
import { createBatchJobsApi } from './batchJobsApi'
import { createForumApi } from './forumApi'
import { createRecommendationsApi } from './recommendationsApi'

export interface VoteStatus {
    hasVoted: boolean
    streak: number
    nextVoteInSeconds: number
    tier: { label: string; threshold: number } | null
    nextTier: { label: string; threshold: number } | null
    voteUrl: string
}

const browserLocation =
    typeof globalThis !== 'undefined' && 'window' in globalThis
        ? globalThis.window.location
        : undefined

function removeTrailingSlashes(value: string): string {
    let result = value
    while (result.length > 1 && result.endsWith('/')) {
        result = result.slice(0, -1)
    }
    return result
}

const API_BASE = inferApiBase(
    import.meta.env.VITE_API_BASE_URL,
    browserLocation,
)

const NORMALIZED_API_BASE = removeTrailingSlashes(API_BASE)

interface BackendGuild {
    id: string
    name: string
    icon: string | null
    owner: boolean
    permissions: string
    features: string[]
    hasBot: boolean
    botInviteUrl?: string
    memberCount?: number | null
    categoryCount?: number | null
    textChannelCount?: number | null
    voiceChannelCount?: number | null
    roleCount?: number | null
    effectiveAccess?: Guild['effectiveAccess']
    canManageRbac?: boolean
}

const mapGuild = (backendGuild: BackendGuild): Guild => {
    const { hasBot, ...rest } = backendGuild
    return {
        ...rest,
        botAdded: hasBot,
    }
}

const apiClient: AxiosInstance = axios.create({
    baseURL: NORMALIZED_API_BASE,
    withCredentials: true,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
})

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (!error.response) {
            return Promise.reject(
                new ApiError(0, 'Unable to connect to the server'),
            )
        }

        const status: number = error.response.status
        const data = error.response.data as
            { error?: string; details?: unknown } | undefined
        const message = data?.error || error.message || 'An error occurred'

        if (
            status === 401 &&
            typeof globalThis !== 'undefined' &&
            'window' in globalThis
        ) {
            const lastRedirect = parseInt(
                sessionStorage.getItem('auth_redirect_ts') || '0',
                10,
            )
            const now = Date.now()
            if (now - lastRedirect > 30_000) {
                sessionStorage.setItem('auth_redirect_ts', String(now))
                globalThis.window.location.assign(
                    `${NORMALIZED_API_BASE}/auth/discord`,
                )
            }
        }

        return Promise.reject(new ApiError(status, message, data?.details))
    },
)

export const api = {
    stats: {
        getPublic: () =>
            apiClient.get<{
                totalGuilds: number
                totalUsers: number
                uptimeSeconds: number
                serversOnline: number
            }>('/stats/public'),
    },

    auth: {
        checkStatus: () =>
            apiClient.get<{ authenticated: boolean; user?: User }>(
                API_ROUTES.AUTH.status(),
            ),
        getUser: async () => {
            const response = await apiClient.get<User>(API_ROUTES.AUTH.user())
            return {
                ...response,
                data: {
                    user: response.data,
                },
            }
        },
        logout: () =>
            apiClient.get<{ success: boolean }>(API_ROUTES.AUTH.logout()),
        getDiscordLoginUrl: () => `${NORMALIZED_API_BASE}/auth/discord`,
    },

    guilds: {
        list: async () => {
            const response = await apiClient.get<{ guilds: BackendGuild[] }>(
                API_ROUTES.GUILDS.list(),
            )
            return {
                ...response,
                data: {
                    guilds: response.data.guilds.map(mapGuild),
                },
            }
        },
        get: async (id: string) => {
            const response = await apiClient.get<BackendGuild>(`/guilds/${id}`)
            return {
                ...response,
                data: {
                    guild: mapGuild(response.data),
                },
            }
        },
        getInvite: (id: string) =>
            apiClient.get<{ inviteUrl: string }>(`/guilds/${id}/invite`),
        getMe: (id: string) =>
            apiClient.get<GuildMemberContext>(`/guilds/${id}/me`),
        getChannels: (id: string) =>
            apiClient.get<{ channels: GuildChannelOption[] }>(
                `/guilds/${id}/channels`,
            ),
        getRoles: (id: string) =>
            apiClient.get<{
                roles: GuildRoleOption[]
                botHighestPosition?: number | null
            }>(`/guilds/${id}/roles`),
        getEmojis: (id: string) =>
            apiClient.get<{ emojis: GuildEmojiOption[] }>(
                `/guilds/${id}/emojis`,
            ),
        getRbac: (id: string) =>
            apiClient.get<{
                guildId: string
                modules: ModuleKey[]
                grants: RoleGrant[]
                roles: GuildRoleOption[]
                effectiveAccess: NonNullable<Guild['effectiveAccess']>
                canManageRbac: boolean
            }>(`/guilds/${id}/rbac`),
        updateRbac: (id: string, grants: RoleGrant[]) =>
            apiClient.put<{ success: boolean; grants: RoleGrant[] }>(
                `/guilds/${id}/rbac`,
                { grants },
            ),
        getSettings: (id: string) =>
            apiClient.get<{ settings: ServerSettings }>(
                `/guilds/${id}/settings`,
            ),
        updateSettings: (id: string, settings: Partial<ServerSettings>) =>
            apiClient.post<{ success: boolean }>(
                `/guilds/${id}/settings`,
                settings,
            ),
        getListing: (id: string) =>
            apiClient.get<{ listing: ServerListing }>(`/guilds/${id}/listing`),
        updateListing: (id: string, listing: Partial<ServerListing>) =>
            apiClient.post<{ success: boolean }>(
                `/guilds/${id}/listing`,
                listing,
            ),
        getAutoplayGenres: (id: string) =>
            apiClient.get<{ genres: string[] }>(
                `/guilds/${id}/autoplay/genres`,
            ),
        updateAutoplayGenres: (id: string, genres: string[]) =>
            apiClient.put<{ genres: string[] }>(
                `/guilds/${id}/autoplay/genres`,
                { genres },
            ),
    },

    me: {
        getVoteStatus: () =>
            apiClient.get<VoteStatus>(API_ROUTES.ME.voteStatus()),
    },

    modules: {
        list: (guildId: string) =>
            apiClient.get<{ modules: Module[] }>(`/guilds/${guildId}/modules`),
        get: (guildId: string, moduleSlug: string) =>
            apiClient.get<{ module: Module }>(
                `/guilds/${guildId}/modules/${moduleSlug}`,
            ),
        toggle: (guildId: string, moduleId: string, enabled: boolean) =>
            apiClient.post<{ success: boolean }>(
                `/guilds/${guildId}/modules/${moduleId}/toggle`,
                { enabled },
            ),
        getSettings: (guildId: string, moduleSlug: string) =>
            apiClient.get<{ settings: Record<string, unknown> }>(
                `/guilds/${guildId}/modules/${moduleSlug}/settings`,
            ),
        updateSettings: (
            guildId: string,
            moduleSlug: string,
            settings: Record<string, unknown>,
        ) =>
            apiClient.post<{ success: boolean }>(
                `/guilds/${guildId}/modules/${moduleSlug}/settings`,
                settings,
            ),
    },

    features: {
        list: async () => {
            const response = await apiClient.get<{
                features: Array<{ name: string; description: string }>
            }>('/features')
            return {
                ...response,
                data: {
                    features: response.data.features.map((f) => ({
                        ...f,
                        isGlobal: false,
                    })) as Feature[],
                },
            }
        },
        getGlobalToggles: () =>
            apiClient.get<GlobalFeatureToggleResponse>('/toggles/global'),
        updateGlobalToggle: (name: string, enabled: boolean) =>
            apiClient.post<{
                success?: boolean
                error?: string
                message?: string
                note?: string
                provider?: GlobalFeatureToggleResponse['provider']
                writable?: boolean
            }>(`/toggles/global/${name}`, {
                enabled,
            }),
    },

    admin: {
        getGuilds: () =>
            apiClient.get<{
                guilds: Array<{
                    id: string
                    name: string
                    iconUrl: string | null
                    memberCount: number | null
                    textChannelCount: number | null
                    voiceChannelCount: number | null
                    roleCount: number | null
                }>
            }>('/admin/guilds'),
    },

    trackHistory: {
        getHistory: (guildId: string, limit = 50, offset = 0) =>
            apiClient.get<{
                history: Array<{
                    trackId: string
                    title: string
                    author: string
                    duration: string
                    url: string
                    timestamp: number
                    playedBy?: string
                }>
                total: number
            }>(
                `/guilds/${guildId}/music/history?limit=${limit}&offset=${offset}`,
            ),
        getStats: (guildId: string) =>
            apiClient.get<{
                stats: {
                    totalTracks: number
                    totalPlayTime: number
                    topArtists: Array<{ artist: string; plays: number }>
                    topTracks: Array<{
                        trackId: string
                        title: string
                        plays: number
                    }>
                    lastUpdated: string
                } | null
            }>(`/guilds/${guildId}/music/history/stats`),
        getTopTracks: (guildId: string, limit = 10) =>
            apiClient.get<{
                tracks: Array<{
                    trackId: string
                    title: string
                    plays: number
                }>
            }>(`/guilds/${guildId}/music/history/top-tracks?limit=${limit}`),
        getTopArtists: (guildId: string, limit = 10) =>
            apiClient.get<{
                artists: Array<{ artist: string; plays: number }>
            }>(`/guilds/${guildId}/music/history/top-artists?limit=${limit}`),
        clearHistory: (guildId: string) =>
            apiClient.delete<{ success: boolean }>(
                `/guilds/${guildId}/music/history`,
            ),
    },

    twitch: {
        status: () => apiClient.get<{ configured: boolean }>('/twitch/status'),
        lookupUser: (login: string) =>
            apiClient.get<{
                id: string
                login: string
                displayName: string
            }>(`/twitch/users?login=${encodeURIComponent(login)}`),
        list: (guildId: string) =>
            apiClient.get<{
                notifications: Array<{
                    id: string
                    guildId: string
                    twitchUserId: string
                    twitchLogin: string
                    discordChannelId: string
                }>
            }>(`/guilds/${guildId}/twitch/notifications`),
        add: (
            guildId: string,
            data: {
                twitchUserId: string
                twitchLogin: string
                discordChannelId: string
            },
        ) =>
            apiClient.post<{ success: boolean }>(
                `/guilds/${guildId}/twitch/notifications`,
                data,
            ),
        remove: (guildId: string, twitchUserId: string) =>
            apiClient.delete<{ success: boolean }>(
                `/guilds/${guildId}/twitch/notifications`,
                { data: { twitchUserId } },
            ),
    },

    lastfm: {
        status: () =>
            apiClient.get<{
                configured: boolean
                linked: boolean
                username: string | null
            }>('/lastfm/status'),
        unlink: () => apiClient.delete<{ success: boolean }>('/lastfm/unlink'),
        getConnectUrl: () => `${NORMALIZED_API_BASE}/lastfm/connect`,
    },

    spotify: {
        status: () =>
            apiClient.get<{
                configured: boolean
                linked: boolean
                username: string | null
            }>('/spotify/status'),
        unlink: () => apiClient.delete<{ success: boolean }>('/spotify/unlink'),
        getConnectUrl: () => `${NORMALIZED_API_BASE}/spotify/connect`,
    },

    lyrics: {
        search: (title: string, artist?: string) => {
            const params = new URLSearchParams({ title })
            if (artist) params.set('artist', artist)
            return apiClient.get<{
                lyrics: string
                title: string
                artist: string
            }>(`/lyrics?${params.toString()}`)
        },
    },

    autoMessages: createAutoMessagesApi(apiClient),
    embeds: createEmbedsApi(apiClient),
    reactionRoles: createReactionRolesApi(apiClient),
    roleGroups: createRoleGroupsApi(apiClient),
    automation: createAutomationApi(apiClient),
    levels: createLevelsApi(apiClient),
    music: createMusicApi(apiClient),
    moderation: createModerationApi(apiClient),
    automod: createAutoModApi(apiClient),
    serverLogs: createLogsApi(apiClient),
    artists: createArtistsApi(apiClient),
    support: createSupportApi(apiClient, NORMALIZED_API_BASE),
    rolesManage: createRolesManageApi(apiClient),
    batchJobs: createBatchJobsApi(apiClient),
    forum: createForumApi(apiClient),
    recommendations: createRecommendationsApi(apiClient),
}

export { ApiError } from './ApiError'
